// owner/ui/useOwnerFormat.js — the shared formatters, bound to the reader's language, with Western digits
// (owner decision 4 Nov 2026). No formatting logic lives here: every function is utils/dateFormatter,
// utils/numberFormatter, utils/currencyFormatter or utils/localeText (house rule: no local formatters).

import { useMemo } from 'react';
import useLanguage from '../../hooks/useLanguage';
import {
  formatDateLatn, formatDayMonthLatn, formatDayOfMonthLatn, formatBusinessMonthLatn, formatMonthName, formatWeekdayLatn, formatYearLatn
} from '../../utils/dateFormatter';
import { formatCountLatn, formatPercentLatn } from '../../utils/numberFormatter';
import { formatSARGrouped, formatSARAmount, sarSuffix } from '../../utils/currencyFormatter';
import { countWith } from '../../utils/localeText';

const useOwnerFormat = () => {
  const { t, language } = useLanguage();
  return useMemo(() => ({
    date: (value) => formatDateLatn(value, language),          // "27 نوفمبر 2026"
    dayMonth: (value) => formatDayMonthLatn(value, language),  // "27 نوفمبر"
    dayOfMonth: (value) => formatDayOfMonthLatn(value),        // "27"
    weekday: (value) => formatWeekdayLatn(value, language),    // "الجمعة"
    year: (value) => formatYearLatn(value),                    // "2027"
    month: (key) => formatBusinessMonthLatn(key, language),    // "نوفمبر 2026"
    monthName: (key) => formatMonthName(key, language),        // "نوفمبر"
    money: (amount) => formatSARGrouped(amount, language),     // "94,380.5 ر.س"
    amount: (amount) => formatSARAmount(amount),               // "94,380.5"
    currency: sarSuffix(language),                             // "ر.س" / "SAR"
    count: (n) => formatCountLatn(n),
    percent: (n) => formatPercentLatn(n),
    nights: (n) => countWith(n, t('ownerApp.common.nights'), formatCountLatn),
    units: (n) => countWith(n, t('ownerApp.common.units'), formatCountLatn),
    bookings: (n) => countWith(n, t('ownerApp.common.bookings'), formatCountLatn),
    guests: (n) => countWith(n, t('ownerApp.common.guests'), formatCountLatn)
  }), [t, language]);
};

export default useOwnerFormat;
